import { t } from "../lib/i18n";
/**
 * Minimal safe Markdown-ish renderer for AI-generated teasers.
 * Supports:
 *  - # H1, ## H2 headings
 *  - **bold** inline
 *  - "**Heading**" at the start of a block as section title (h3)
 *  - "- " bullet lists (preserves leading "✓" checkmarks)
 *  - GitHub-style pipe tables:
 *      | Col1 | Col2 |
 *      |---|---|
 *      | a | b |
 *  - blank line = paragraph break
 */

export default function TeaserMarkdown({ text, stripLeadingHeading = false }) {
  if (!text) return null;

  let source = text;
  if (stripLeadingHeading) {
    source = source.trimStart().replace(/^#{1,4}\s+[^\n]+\n+/, "");
  }
  const blocks = splitBlocks(source);

  return (
    <div className="space-y-5 text-foreground">
      {blocks.map((block, i) => renderBlock(block, i))}
    </div>
  );
}

function splitBlocks(text) {
  const raw = text.replace(/\r\n/g, "\n").split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  // Further split: if a block starts with # / ## heading and has more content, isolate the heading
  const out = [];
  for (const b of raw) {
    const m = b.match(/^(#{1,4})\s+([^\n]+)\n([\s\S]+)$/);
    if (m) {
      out.push(`${m[1]} ${m[2]}`);
      out.push(m[3].trim());
    } else {
      out.push(b);
    }
  }
  return out;
}

function renderBlock(block, key) {
  // H1
  if (/^#\s+/.test(block) && !block.includes("\n")) {
    const txt = block.replace(/^#\s+/, "");
    return (
      <h2 key={key} className="font-serif-display text-3xl md:text-4xl tracking-tight leading-tight">
        {renderInline(txt)}
      </h2>
    );
  }
  // H2
  if (/^##\s+/.test(block) && !block.includes("\n")) {
    const txt = block.replace(/^##\s+/, "");
    return <h3 key={key} className="font-serif-display text-2xl">{renderInline(txt)}</h3>;
  }
  // H4
  if (/^####\s+/.test(block) && !block.includes("\n")) {
    const txt = block.replace(/^####\s+/, "");
    return <h4 key={key} className="font-serif-display text-lg">{renderInline(txt)}</h4>;
  }
  // H3
  if (/^###\s+/.test(block) && !block.includes("\n")) {
    const txt = block.replace(/^###\s+/, "");
    return <h3 key={key} className="font-serif-display text-xl">{renderInline(txt)}</h3>;
  }
  // Table
  if (isTable(block)) {
    return <TableBlock key={key} block={block} />;
  }
  // Bullets list — supports "- ", "* ", or "✓ " line-starts
  if (/^([-*]\s+|✓\s+)/.test(block.split("\n")[0])) {
    const rawLines = block.split("\n").filter((l) => l.trim());
    const items = rawLines.map((l) => {
      const hadCheck = /^✓\s+/.test(l) || /^[-*]\s+✓\s+/.test(l);
      const content = l.replace(/^[-*]\s+/, "").replace(/^✓\s+/, "").trim();
      return { hadCheck, content };
    });
    return (
      <ul key={key} className="space-y-2">
        {items.map((it, j) => (
          <li key={j} className="flex items-start gap-3 text-sm leading-relaxed">
            {it.hadCheck ? (
              <span className="flex-shrink-0 mt-1 text-forest">✓</span>
            ) : (
              <span className="flex-shrink-0 mt-[8px] w-1.5 h-1.5 bg-[#E4B54B]" aria-hidden="true" />
            )}
            <span className="text-muted-foreground">{renderInline(it.content)}</span>
          </li>
        ))}
      </ul>
    );
  }
  // **Heading**  at the start of the block → section title (only if bold covers the whole first line)
  const headMatch = block.match(/^\*\*([^*\n]+?)\*\*[ \t]*(?:\n([\s\S]*))?$/);
  if (headMatch) {
    const [, head, body] = headMatch;
    return (
      <div key={key}>
        <h3 className="font-serif-display text-2xl mb-2">{head}</h3>
        {body && (
          <p className="leading-relaxed text-muted-foreground whitespace-pre-line">
            {renderInline(body.trim())}
          </p>
        )}
      </div>
    );
  }
  // default paragraph
  return (
    <p key={key} className="leading-relaxed text-muted-foreground whitespace-pre-line">
      {renderInline(block)}
    </p>
  );
}

function isTable(block) {
  const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return false;
  return lines[0].includes("|") && /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(lines[1]);
}

function TableBlock({ block }) {
  const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
  const headers = parseRow(lines[0]);
  const rows = lines.slice(2).map(parseRow);
  return (
    <div className="hairline overflow-x-auto bg-card my-2">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-bone">
            {headers.map((h, i) => (
              <th key={i} className="font-mono-label text-muted-foreground text-left px-4 py-3 hairline-b">
                {renderInline(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i > 0 ? "hairline-t" : ""}>
              {r.map((c, j) => (
                <td key={j} className={`px-4 py-3 ${j === 0 ? "font-medium" : "font-serif-display text-base"}`}>
                  {renderInline(c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function parseRow(line) {
  return line
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
}

function renderInline(s) {
  const parts = s.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const m = p.match(/^\*\*(.+)\*\*$/);
    if (m) return <strong key={i} className="text-foreground font-medium">{m[1]}</strong>;
    return <span key={i}>{p}</span>;
  });
}
